import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

export interface ShortcutContext {
  focusedRowKey: string | null;
  focusedIndex: number;
  focusedItem: any | null;
}

export interface CustomShortcut {
  keys: string;
  handler: (ctx: ShortcutContext) => void;
}

interface UseTableKeyboardNavigationOptions<T = any> {
  // Required — every table has these
  items: T[];
  getRowKey: (item: T) => string;
  onExpand: (rowKey: string) => void;
  expandedRowKey: string | null;

  // Disable all shortcuts (e.g. modal open)
  disabled?: boolean;

  // Opt-in: Selection
  onSelect?: (item: T) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;

  // Opt-in: Pagination
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;

  // Opt-in: Help dialog toggle
  onToggleHelp?: () => void;

  // Opt-in: Table-specific shortcuts
  customShortcuts?: CustomShortcut[];
}

export function useTableKeyboardNavigation<T = any>({
  items,
  getRowKey,
  onExpand,
  expandedRowKey,
  disabled = false,
  onSelect,
  onSelectAll,
  onDeselectAll,
  currentPage,
  totalPages,
  onPageChange,
  onToggleHelp,
  customShortcuts,
}: UseTableKeyboardNavigationOptions<T>) {
  const [focusedRowKey, setFocusedRowKey] = useState<string | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const lastFocusedIndexRef = useRef<number>(-1);

  const focusedIndex = items.findIndex(
    item => getRowKey(item) === focusedRowKey,
  );

  // Track last known index so we can recover when a row disappears
  useEffect(() => {
    if (focusedIndex !== -1) {
      lastFocusedIndexRef.current = focusedIndex;
    }
  }, [focusedIndex]);

  // When focused row disappears, move focus to the next row at the same position
  useEffect(() => {
    if (focusedRowKey && items.length > 0) {
      const stillExists = items.some(item => getRowKey(item) === focusedRowKey);
      if (!stillExists) {
        const targetIndex = Math.min(
          lastFocusedIndexRef.current,
          items.length - 1,
        );
        setFocusedRowKey(
          targetIndex >= 0 ? getRowKey(items[targetIndex]) : null,
        );
      }
    } else if (focusedRowKey && items.length === 0) {
      setFocusedRowKey(null);
    }
  }, [items, focusedRowKey, getRowKey]);

  // Reset focus on page change
  useEffect(() => {
    if (currentPage !== undefined) {
      setFocusedRowKey(null);
    }
  }, [currentPage]);

  // Scroll focused row into view
  useEffect(() => {
    if (!focusedRowKey || !tableContainerRef.current) return;
    const row = tableContainerRef.current.querySelector(
      `[data-rowkey="${focusedRowKey}"]`,
    );
    if (row) {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedRowKey]);

  const isInputFocused = useCallback(() => {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName.toLowerCase();
    return (
      tag === 'input' ||
      tag === 'textarea' ||
      tag === 'select' ||
      active.getAttribute('role') === 'combobox' ||
      active.getAttribute('contenteditable') === 'true'
    );
  }, []);

  const hotkeysOptions = {
    enabled: !disabled,
    enableOnFormTags: false as const,
    preventDefault: true,
  };

  const moveFocusDown = useCallback(() => {
    if (items.length === 0) return;
    if (focusedRowKey === null) {
      setFocusedRowKey(getRowKey(items[0]));
    } else {
      const nextIndex = Math.min(focusedIndex + 1, items.length - 1);
      setFocusedRowKey(getRowKey(items[nextIndex]));
    }
  }, [items, focusedRowKey, focusedIndex, getRowKey]);

  const moveFocusUp = useCallback(() => {
    if (items.length === 0) return;
    if (focusedRowKey === null) {
      setFocusedRowKey(getRowKey(items[items.length - 1]));
    } else {
      const prevIndex = Math.max(focusedIndex - 1, 0);
      setFocusedRowKey(getRowKey(items[prevIndex]));
    }
  }, [items, focusedRowKey, focusedIndex, getRowKey]);

  // ArrowDown - move focus to next row
  useHotkeys('ArrowDown', moveFocusDown, { ...hotkeysOptions }, [
    moveFocusDown,
    disabled,
  ]);

  // ArrowUp - move focus to previous row
  useHotkeys('ArrowUp', moveFocusUp, { ...hotkeysOptions }, [
    moveFocusUp,
    disabled,
  ]);

  // j / k — vim-style aliases for down/up (guarded so typing is unaffected)
  useHotkeys(
    'j',
    () => {
      if (isInputFocused()) return;
      moveFocusDown();
    },
    { ...hotkeysOptions },
    [moveFocusDown, disabled],
  );

  useHotkeys(
    'k',
    () => {
      if (isInputFocused()) return;
      moveFocusUp();
    },
    { ...hotkeysOptions },
    [moveFocusUp, disabled],
  );

  // Home - focus first row
  useHotkeys(
    'Home',
    () => {
      if (items.length === 0) return;
      setFocusedRowKey(getRowKey(items[0]));
    },
    { ...hotkeysOptions },
    [items, getRowKey, disabled],
  );

  // End - focus last row
  useHotkeys(
    'End',
    () => {
      if (items.length === 0) return;
      setFocusedRowKey(getRowKey(items[items.length - 1]));
    },
    { ...hotkeysOptions },
    [items, getRowKey, disabled],
  );

  // Enter - toggle expand/collapse
  useHotkeys(
    'Enter',
    () => {
      if (focusedRowKey === null) return;
      onExpand(focusedRowKey);
    },
    { ...hotkeysOptions },
    [focusedRowKey, onExpand, disabled],
  );

  // ArrowRight - expand focused row (no-op if already expanded)
  useHotkeys(
    'ArrowRight',
    () => {
      if (focusedRowKey === null) return;
      if (expandedRowKey !== focusedRowKey) {
        onExpand(focusedRowKey);
      }
    },
    { ...hotkeysOptions },
    [focusedRowKey, expandedRowKey, onExpand, disabled],
  );

  // ArrowLeft - collapse focused row (no-op if already collapsed)
  useHotkeys(
    'ArrowLeft',
    () => {
      if (focusedRowKey === null) return;
      if (expandedRowKey === focusedRowKey) {
        onExpand(focusedRowKey);
      }
    },
    { ...hotkeysOptions },
    [focusedRowKey, expandedRowKey, onExpand, disabled],
  );

  // Space - toggle checkbox (opt-in)
  useHotkeys(
    'space',
    () => {
      if (!onSelect) return;
      if (focusedRowKey === null || focusedIndex === -1) return;
      onSelect(items[focusedIndex]);
    },
    { ...hotkeysOptions },
    [focusedRowKey, focusedIndex, items, onSelect, disabled],
  );

  // Escape - collapse expanded row or deselect all
  useHotkeys(
    'Escape',
    () => {
      if (expandedRowKey !== null) {
        onExpand(expandedRowKey);
      } else if (onDeselectAll) {
        onDeselectAll();
      }
    },
    { ...hotkeysOptions, enableOnFormTags: true as any },
    [expandedRowKey, onExpand, onDeselectAll, disabled],
  );

  // Ctrl+A / Cmd+A - select all (opt-in)
  useHotkeys(
    'mod+a',
    () => {
      if (!onSelectAll) return;
      onSelectAll();
    },
    { ...hotkeysOptions },
    [onSelectAll, disabled],
  );

  // PageDown - next page (opt-in)
  useHotkeys(
    'PageDown',
    () => {
      if (
        !onPageChange ||
        currentPage === undefined ||
        totalPages === undefined
      )
        return;
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    },
    { ...hotkeysOptions },
    [currentPage, totalPages, onPageChange, disabled],
  );

  // PageUp - previous page (opt-in)
  useHotkeys(
    'PageUp',
    () => {
      if (!onPageChange || currentPage === undefined) return;
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    },
    { ...hotkeysOptions },
    [currentPage, onPageChange, disabled],
  );

  // ? (Shift+/) - toggle help overlay (opt-in)
  useHotkeys(
    'shift+/',
    () => {
      if (!onToggleHelp) return;
      if (isInputFocused()) return;
      onToggleHelp();
    },
    { ...hotkeysOptions, enableOnFormTags: false as const },
    [onToggleHelp, disabled],
  );

  // Custom shortcuts — single useHotkeys call with comma-separated keys
  const customShortcutsRef = useRef(customShortcuts);
  customShortcutsRef.current = customShortcuts;

  const customKeys = customShortcuts?.map(s => s.keys).join(',') || '';

  useHotkeys(
    customKeys || 'never-match-this-key',
    (event: KeyboardEvent) => {
      if (!customShortcutsRef.current) return;
      if (isInputFocused()) return;

      const hasMod = event.ctrlKey || event.metaKey;
      const pressedKey = event.key.toLowerCase();

      const ctx: ShortcutContext = {
        focusedRowKey,
        focusedIndex,
        focusedItem: focusedIndex >= 0 ? items[focusedIndex] : null,
      };

      for (const shortcut of customShortcutsRef.current) {
        const parts = shortcut.keys.toLowerCase().split('+');
        const key = parts[parts.length - 1];
        const needsMod = parts.includes('mod');
        const needsShift = parts.includes('shift');
        const needsAlt = parts.includes('alt');

        if (
          pressedKey === key &&
          hasMod === needsMod &&
          event.shiftKey === needsShift &&
          event.altKey === needsAlt
        ) {
          shortcut.handler(ctx);
          return;
        }
      }
    },
    { ...hotkeysOptions, enableOnFormTags: false as const },
    [customKeys, focusedRowKey, focusedIndex, items, disabled],
  );

  const isRowFocused = useCallback(
    (rowKey: string) => focusedRowKey === rowKey,
    [focusedRowKey],
  );

  return {
    focusedRowKey,
    setFocusedRowKey,
    isRowFocused,
    tableContainerRef,
  };
}
